'use client';

import { useJobs } from '@/hooks/useJob';
import { Briefcase, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from '@ui/components/ui/sidebar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ui/components/ui/select';
import { Button } from '@ui/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/ui/tooltip';
import { cn } from '@ui/lib/utils';

export function NavJobSelector() {
  const { jobs, setActiveJob, clearActiveJob } = useJobs();
  const activeJob = jobs?.find((job) => job.selected);
  const { state } = useSidebar();
  const router = useRouter();
  const t = useTranslations('Jobs');

  const handleSelectJob = (jobId: string) => {
    const numId = Number(jobId);
    setActiveJob(numId);
  };

  const handleClearJob = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    clearActiveJob();
  };

  const handleJobClick = () => {
    if (activeJob) {
      router.push(`/jobs/${activeJob.id}`);
    }
  };

  if (!jobs || jobs.length === 0) {
    return null;
  }

  const isCollapsed = state === 'collapsed';

  return (
    <SidebarGroup data-tutorial="job-selector">
      <SidebarGroupLabel>{t('activeJob')}</SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          <SidebarMenuItem>
            {activeJob ? (
              isCollapsed ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <SidebarMenuButton asChild>
                      <button
                        className="flex items-center justify-between w-full ring-secondary ring-1"
                        title={activeJob.title}
                        onClick={handleJobClick}
                      >
                        <Briefcase className="h-4 w-4" />
                      </button>
                    </SidebarMenuButton>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4" />
                    <span>{activeJob.title}</span>
                    <Button variant="ghost" size="sm" className="h-5 w-5 p-0 ml-2 hover:bg-destructive/10" onClick={handleClearJob}>
                      <X className="h-3 w-3" />
                    </Button>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <div className="relative">
                  <SidebarMenuButton asChild>
                    <button className="w-full justify-start text-left" onClick={handleJobClick}>
                      <Briefcase className="h-4 w-4 shrink-0" />
                      <span className="truncate flex-1 mr-5">{activeJob.title}</span>
                    </button>
                  </SidebarMenuButton>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 hover:bg-destructive/10"
                    onClick={handleClearJob}
                    title="Clear job selection"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )
            ) : (
              <SidebarMenuButton asChild>
                <Select value="" onValueChange={handleSelectJob}>
                  <SelectTrigger className={cn('w-full border-0 shadow-none p-2 min-w-0 bg-transparent')} iconOnly={isCollapsed}>
                    <Briefcase className="w-4" />
                    {!isCollapsed && <SelectValue placeholder="Select a job..." />}
                  </SelectTrigger>
                  <SelectContent>
                    {jobs.map((job) => (
                      <SelectItem key={job.id} value={String(job.id)}>
                        {job.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </SidebarMenuButton>
            )}
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
